import { TrendingDown, TrendingUp } from "lucide-react";
import type { MonthInsights, DailyTotal } from "@/lib/expenses";
import { formatMoney } from "@/lib/format";

export function SummaryHeader({
  insights,
  busiestDay,
  todayCents,
}: {
  insights: MonthInsights;
  busiestDay: DailyTotal | null;
  todayCents: number;
}) {
  const {
    totalCents,
    currency,
    count,
    isCurrentMonth,
    dailyPaceCents,
    projectedCents,
    deltaPct,
  } = insights;

  const up = deltaPct != null && deltaPct > 0;
  const showTrend = deltaPct != null && deltaPct !== 0;

  return (
    <div className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-[#FF8A5B] to-[#F0492F] p-6 text-white shadow-lg shadow-orange-500/20">
      <div className="absolute -right-10 -top-12 size-44 rounded-full bg-white/10 blur-xl" />

      <div className="relative flex items-start justify-between">
        <p className="text-sm font-medium text-white/80">
          {isCurrentMonth ? "Spent so far" : "Total spent"}
        </p>
        {showTrend && (
          <span className="flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold">
            {up ? (
              <TrendingUp className="size-3.5" />
            ) : (
              <TrendingDown className="size-3.5" />
            )}
            {Math.abs(deltaPct)}% vs last mo.
          </span>
        )}
      </div>

      <p className="relative mt-1 text-[2.75rem] font-bold leading-none tracking-tight tabular-nums">
        {formatMoney(totalCents, currency)}
      </p>
      <p className="relative mt-1.5 text-sm text-white/85">
        {count} {count === 1 ? "expense" : "expenses"}
        {todayCents > 0 && (
          <>
            {" · "}
            <span className="font-semibold tabular-nums">
              {formatMoney(todayCents, currency)}
            </span>{" "}
            today
          </>
        )}
      </p>

      {/* Two-up stat tiles: pace and where the month is heading. */}
      <div className="relative mt-4 grid grid-cols-2 gap-2">
        <div className="rounded-2xl bg-white/15 px-3.5 py-2.5">
          <p className="text-xs text-white/75">Avg / day</p>
          <p className="mt-0.5 font-semibold tabular-nums">
            {formatMoney(dailyPaceCents, currency)}
          </p>
        </div>
        <div className="rounded-2xl bg-white/15 px-3.5 py-2.5">
          <p className="text-xs text-white/75">
            {isCurrentMonth ? "Projected" : "Busiest day"}
          </p>
          <p className="mt-0.5 font-semibold tabular-nums">
            {isCurrentMonth
              ? formatMoney(projectedCents, currency)
              : busiestDay
                ? formatMoney(busiestDay.totalCents, currency)
                : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}
